import type { TableRowBlock, TableBlock } from '@atlas/renderer';
import { Editor, Range, Transforms, Path } from 'slate';
import areSamePoints from '@atlas/tool/box/slate/areSamePoints';
import getNode from '@atlas/tool/box/slate/getNode';
import removeTopLevelBlocks from '@atlas/tool/box/slate/removeTopLevelBlocks';
import sortSelection from '@atlas/tool/box/slate/sortSelection';

export default function deleteTablePart(editor: Editor, selection: Range) {
    const [selectionStart, selectionEnd] = sortSelection(selection);
    const tablePath = [selection.anchor.path[0]];
    const tableStart = Editor.start(editor, tablePath);
    const tableEnd = Editor.end(editor, tablePath);
    const fromTableStart = areSamePoints(selectionStart, tableStart);
    const toTableEnd = areSamePoints(selectionEnd, tableEnd);
    const fromRowStart = areSamePoints(selectionStart, Editor.start(editor, selectionStart.path.slice(0, 2)));
    const toRowEnd = areSamePoints(selectionEnd, Editor.end(editor, selectionEnd.path.slice(0, 2)));

    if (fromTableStart && toTableEnd) {
        // table is selected from the start to the end
        removeTopLevelBlocks(editor, selectionStart.path[0], selectionStart.path[0], true);
        return;
    }

    const isSameRow = Path.equals(selectionStart.path.slice(0, 2), selectionEnd.path.slice(0, 2));

    if (isSameRow) {
        if (fromRowStart && toRowEnd) {
            // row is selected from the start to the end
            Transforms.removeNodes(editor, { at: selectionStart.path.slice(0, 2) });
            return;
        }

        const isSameCell = isSameRow && selectionStart.path[2] === selectionEnd.path[2];

        if (isSameCell) {
            // normal deletion inside of a single cell
            Transforms.delete(editor, {
                at: { anchor: selectionStart, focus: selectionEnd },
            });

            return;
        }
    }

    const table = getNode<TableBlock>(editor, tablePath);
    if (!table) return;

    const firstSelectedRowIndex = selectionStart.path[1];
    const lastSelectedRowIndex = selectionEnd.path[1];

    const firstSelectedCellIndex = selectionStart.path[2];
    const lastSelectedCellIndex = selectionEnd.path[2];

    const newTable: TableBlock = {
        ...table,
        children: table.children
            .map((row, rowIndex) => {
                if (rowIndex < firstSelectedRowIndex) return row;
                if (rowIndex > lastSelectedRowIndex) return row;

                if (rowIndex > firstSelectedRowIndex && rowIndex < lastSelectedRowIndex) {
                    return null;
                }

                if (rowIndex === firstSelectedRowIndex && fromRowStart && lastSelectedRowIndex > rowIndex) {
                    return null;
                }

                if (rowIndex === lastSelectedRowIndex && toRowEnd && firstSelectedRowIndex < rowIndex) {
                    return null;
                }

                return {
                    ...row,
                    children: row.children.map((cell, cellIndex) => {
                        if (firstSelectedRowIndex === rowIndex && cellIndex < firstSelectedCellIndex) {
                            return cell;
                        }

                        if (lastSelectedRowIndex === rowIndex && cellIndex > lastSelectedCellIndex) {
                            return cell;
                        }

                        if (firstSelectedRowIndex === rowIndex && cellIndex === firstSelectedCellIndex) {
                            const childIndex = selectionStart.path[3];
                            const selectedChild = cell.children[childIndex];
                            return {
                                ...cell,
                                children: cell.children.slice(0, childIndex).concat({
                                    ...selectedChild,
                                    text: selectedChild.text.slice(0, selectionStart.offset),
                                }),
                            };
                        }

                        if (lastSelectedRowIndex === rowIndex && cellIndex === lastSelectedCellIndex) {
                            const childIndex = selectionEnd.path[3];
                            const selectedChild = cell.children[childIndex];
                            return {
                                ...cell,
                                children: [
                                    {
                                        ...selectedChild,
                                        text: selectedChild.text.slice(selectionEnd.offset),
                                    },
                                ].concat(cell.children.slice(childIndex + 1)),
                            };
                        }

                        return { ...cell, children: [{ text: '' }] };
                    }),
                };
            })
            .filter(Boolean) as TableRowBlock[],
    };

    Transforms.insertNodes(editor, [newTable], { at: tablePath });
    removeTopLevelBlocks(editor, tablePath[0] + 1);
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('deleteTablePart', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
